import { Heart, MessageCircle, Repeat2, Share, Bookmark, BarChart3 } from 'lucide-react'
import { useState } from 'react'
import RepostDropdown from './RepostDropdown'
import VerificationShieldModal from './VerificationShieldModal'

interface PostActionsProps {
  stats?: {
    likes?: number
    comments?: number
    reposts?: number
    views?: number
  }
  onLike?: () => void
  onComment?: () => void
  onRepost?: () => void
  onQuote?: () => void
  onShare?: () => void
  onBookmark?: () => void
  onAnalytics?: () => void
  isLiked?: boolean
  isBookmarked?: boolean
  isReposted?: boolean
  loading?: {
    like?: boolean
    save?: boolean
    repost?: boolean
  }
  className?: string
  isGuest?: boolean
  hasMedia?: boolean
  onRecordCode?: string
  location?: { address?: string }
  timeAgo?: string
  capturedAt?: string
  createdAt?: string
}

export default function PostActions({
  stats = {},
  onLike,
  onComment,
  onRepost,
  onQuote,
  onShare,
  onBookmark,
  onAnalytics,
  isLiked = false,
  isBookmarked = false,
  isReposted = false,
  loading = {},
  className = '',
  isGuest = false,
  hasMedia = false,
  onRecordCode,
  location,
  timeAgo,
  capturedAt,
  createdAt,
}: PostActionsProps) {
  const [showRepostDropdown, setShowRepostDropdown] = useState(false)

  const formatCount = (count: number = 0) => {
    if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K`
    return count > 0 ? count.toString() : ''
  }

  const iconStyle = { width: 22, height: 22, strokeWidth: 2.25 }

  return (
    <div className={`flex items-center justify-between mt-1 pt-1 border-t border-gray-100 -ml-2 -mr-2 ${className}`}>
      {/* Shield — Content Authenticity (shared component) */}
      <div className="flex items-center gap-1 flex-1 justify-center">
        <VerificationShieldModal
          code={onRecordCode}
          hasMedia={hasMedia}
          capturedAt={capturedAt}
          createdAt={createdAt}
        />
      </div>

      {/* Comment */}
      <button
        onClick={onComment}
        className={`flex items-center gap-1 text-gray-500 flex-1 justify-center ${
          isGuest ? 'cursor-pointer' : 'hover:text-blue-500'
        }`}
        title={isGuest ? 'Sign in to comment' : undefined}
      >
        <MessageCircle style={iconStyle} />
        <span className="text-sm">{formatCount(stats.comments)}</span>
      </button>

      {/* Repost */}
      <div className="flex items-center gap-1 flex-1 justify-center relative">
        <button
          onClick={(e) => { e.stopPropagation(); setShowRepostDropdown(!showRepostDropdown) }}
          disabled={loading.repost}
          className={`flex items-center gap-1 ${
            isReposted ? 'text-blue-500' : 'text-gray-500'
          } ${loading.repost ? 'opacity-50 cursor-not-allowed' : ''} ${
            !isGuest && !loading.repost ? 'hover:text-blue-500' : ''
          } ${isGuest ? 'cursor-pointer' : ''}`}
          title={isGuest ? 'Sign in to repost' : undefined}
        >
          <Repeat2 style={iconStyle} className={loading.repost ? 'animate-spin' : ''} />
          <span className={`text-sm ${isReposted ? 'text-blue-500' : ''}`}>
            {formatCount(stats.reposts)}
          </span>
        </button>
        <RepostDropdown
          isOpen={showRepostDropdown}
          onClose={() => setShowRepostDropdown(false)}
          onRepost={() => onRepost?.()}
          onQuote={() => onQuote?.()}
          isReposted={isReposted}
        />
      </div>

      {/* Like */}
      <button
        onClick={onLike}
        disabled={loading.like}
        className={`flex items-center gap-1 flex-1 justify-center ${
          isLiked ? 'text-red-500' : 'text-gray-500'
        } ${loading.like ? 'opacity-50 cursor-not-allowed' : ''} ${
          !isGuest && !loading.like ? 'hover:text-red-500' : ''
        } ${isGuest ? 'cursor-pointer' : ''}`}
        title={isGuest ? 'Sign in to like' : undefined}
      >
        <Heart style={iconStyle} className={`${isLiked ? 'fill-red-500' : ''} ${loading.like ? 'animate-pulse' : ''}`} />
        <span className={`text-sm ${isLiked ? 'text-red-500' : ''}`}>
          {formatCount(stats.likes)}
        </span>
      </button>

      {/* Analytics */}
      <button
        onClick={(e) => { e.stopPropagation(); onAnalytics?.() }}
        className="flex items-center gap-1 text-gray-500 flex-1 justify-center hover:text-blue-500"
      >
        <BarChart3 style={iconStyle} />
        <span className="text-sm">{formatCount(stats.views)}</span>
      </button>

      {/* Bookmark */}
      <button
        onClick={onBookmark}
        disabled={loading.save}
        className={`flex items-center gap-1 flex-1 justify-center ${
          isBookmarked ? 'text-onrecord-orange' : 'text-gray-500'
        } ${loading.save ? 'opacity-50 cursor-not-allowed' : ''} ${
          !isGuest && !loading.save ? 'hover:text-onrecord-orange' : ''
        } ${isGuest ? 'cursor-pointer' : ''}`}
        title={isGuest ? 'Sign in to bookmark' : undefined}
      >
        <Bookmark style={iconStyle} className={`${isBookmarked ? 'fill-onrecord-orange' : ''} ${loading.save ? 'animate-pulse' : ''}`} />
      </button>

      {/* Share */}
      <button
        onClick={onShare}
        className={`flex items-center gap-1 text-gray-500 flex-1 justify-center ${
          isGuest ? 'cursor-pointer' : 'hover:text-onrecord-orange'
        }`}
        title={isGuest ? 'Sign in to share' : undefined}
      >
        <Share style={iconStyle} />
      </button>
    </div>
  )
}
